import { CFDFile, RunConfigCase, RunStatus, WorkflowRunGroup } from '@aero-platform/shared'
import { Column } from 'primereact/column'
import { confirmDialog } from 'primereact/confirmdialog'
import { DataTable, DataTableExpandedRows } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { classNames } from 'primereact/utils'
import { FC, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import {
  useAddWorkflowRunGroup,
  useRefreshMethodology,
  useUpdateRunConfigCase,
  addSimChangeMetadata,
  getSimChangeMetadata
} from '../../api/submission-API'
import {
  getMAVCase,
  isRunGroupMAVCompleted,
  MAVParam,
  computeSupersededIds
} from '../../types/submission-util'
import { SimChangeRecord } from '../../types/simChangeTypes'
import { CompactTableHeaderClassName, NASCAR28Teams } from '../../utils/constants'
import { currentUserState, currentVehicleState, isF1TeamState } from '../../utils/state-atoms'
import { canMutate } from '../../utils/roles'
import IconButton from '../buttons/IconButton'
import {
  CollapseIcon,
  DeleteIcon,
  EditIcon,
  ExpandIcon,
  EyeIcon,
  MAVIcon,
  PlusIcon,
  RedoIcon
} from '../Icons'
import Modal from '../Modal'
import StateRowExpansionTemplate, {
  determineWorkflowGroupStatus,
  getMatchingWorkflowRunGroup
} from './CaseRowExpansion'
import { runCaseNameBodyTemplates, getDraftingOffsetInfo } from './DataTableBodyTemplate'
import PeriviewModal from './PeriviewModal'
import ResubmitCaseModal from './ResubmitCaseModal'
import { protoService } from '../../proto/ProtoService'
import { cloneRunConfigCase } from '../../types/util'
import { safeStringify } from '../../utils/safeJson'
import ViewLogsButton from './ViewLogsButton'

type CaseGroupTableProps = {
  runId: string
  selectedSystemRunId: string
  submissionGroupName: string
  workflowRunGroups: WorkflowRunGroup[]
  // Every container's run groups (all submissionRunGroups flattened). Used so superseding is
  // computed across the whole submission — a per-case resubmit lands in a separate container,
  // and without the global view the run it replaced would still show as active here.
  allWorkflowRunGroups?: WorkflowRunGroup[]
  isCommitting: boolean
  isDeletingCase: boolean
  isUpdatingCase: boolean
  raceTeamId: string
  speedType?: string
  onEditCase: (
    submissionGroupName: string,
    caseName: string,
    caseIndex: number,
    caseKey?: string
  ) => void
  onDeleteCase: (
    submissionGroupName: string,
    caseName: string,
    caseIndex: number,
    caseKey?: string
  ) => void
  showAdditionalReferenceRunsModal: string
  setShowAdditionalReferenceRunsModal: (value: string) => void
  onLogModalStateChange?: (isOpen: boolean) => void
  // systemRunIds of the cases currently selected for a bulk resubmit (lifted to the parent).
  selectedCaseIds?: string[]
  onSelectedCaseIdsChange?: (ids: string[]) => void
}

const CaseGroupTable: FC<CaseGroupTableProps> = ({
  runId,
  selectedSystemRunId,
  workflowRunGroups,
  allWorkflowRunGroups,
  submissionGroupName,
  isCommitting,
  isDeletingCase,
  isUpdatingCase,
  raceTeamId,
  speedType,
  onEditCase,
  onDeleteCase,
  showAdditionalReferenceRunsModal,
  setShowAdditionalReferenceRunsModal,
  onLogModalStateChange,
  selectedCaseIds,
  onSelectedCaseIdsChange
}: CaseGroupTableProps) => {
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({})
  const [expandedPriorRows, setExpandedPriorRows] = useState<DataTableExpandedRows>({})
  const [priorSectionOpen, setPriorSectionOpen] = useState(false)
  const [caseNameToShow, setCaseNameToShow] = useState<string>('')
  const [baselineRunId] = useState<string>('')
  const [caseNameForPariview] = useState<string>()
  const [caseIdForPariview] = useState<string>()
  const [errorResultText] = useState<string>()
  const [showCaseDetail, setShowCaseDetail] = useState(false)
  const [showErrorResultModal, setShowErrorResultModal] = useState(false)
  const [showPeriviewModal, setShowPeriviewModal] = useState(false)
  const [simChangeRecords, setSimChangeRecords] = useState<SimChangeRecord[]>([])
  const [showResubmitModal, setShowResubmitModal] = useState(false)
  const [resubmitCaseData, setResubmitCaseData] = useState<WorkflowRunGroup | null>(null)
  const [isSubmittingResubmit, setIsSubmittingResubmit] = useState(false)
  const isF1Team = useRecoilValue(isF1TeamState)
  const currentUser = useRecoilValue(currentUserState)
  const userCanMutate = canMutate(currentUser?.role?.roleId)
  const currentVehicle = useRecoilValue(currentVehicleState)
  const caseNameRef = useRef(null)
  // Tracks the selectedSystemRunId that has already been auto-expanded/scrolled.
  // Prevents the auto-refresh poll (which produces a new workflowRunGroups
  // reference every few seconds) from re-collapsing rows the user expanded.
  const handledSelectionRef = useRef<string>('')
  const [{ loading: submittingTask }, addWorkflowRunGroup] = useAddWorkflowRunGroup()
  const [, refreshMethodology] = useRefreshMethodology()
  const [, updateRunConfigCase] = useUpdateRunConfigCase()

  // Load SIM change metadata for this submission
  useEffect(() => {
    if (runId) {
      getSimChangeMetadata(runId)
        .then(records => setSimChangeRecords(records || []))
        .catch(() => setSimChangeRecords([]))
    }
  }, [runId])

  /**
   * Detects if the SIM file changed between the superseded (prior) run and the active run
   * for a given case name. Returns true if the sim template file differs.
   */
  const hasSimFileChanged = (wrg: WorkflowRunGroup): boolean => {
    const caseName = wrg.runGroupCase?.name || wrg.name
    const currentSimFile = wrg.runGroupCase?.simTemplate?.file || ''

    // Find prior (superseded) runs for the same case name across the whole submission
    const priorRuns = globalRunGroups.filter(
      other =>
        (other.runGroupCase?.name || other.name) === caseName &&
        other.systemRunId !== wrg.systemRunId &&
        supersededIds.has(other.systemRunId)
    )

    if (priorRuns.length === 0) return false

    // Compare against the most recent prior run (last in array order)
    const mostRecentPrior = priorRuns[priorRuns.length - 1]
    const priorSimFile = mostRecentPrior.runGroupCase?.simTemplate?.file || ''

    return currentSimFile !== priorSimFile && priorSimFile !== ''
  }

  /**
   * Gets the SIM change record (with reason) for a specific workflow run group.
   */
  const getSimChangeRecord = (wrg: WorkflowRunGroup): SimChangeRecord | undefined => {
    const caseName = wrg.runGroupCase?.name || wrg.name
    // Return the most recent matching record (last in array) to avoid showing stale entries
    const matching = simChangeRecords.filter(
      r => r.caseName === caseName && (r.systemRunId === wrg.systemRunId || !r.systemRunId)
    )
    return matching.length > 0 ? matching[matching.length - 1] : undefined
  }

  // Determine which run groups are superseded (older duplicates with the same case key).
  // Computed across the ENTIRE submission (all containers) so a per-case resubmit — which the
  // server appends as a separate "Resubmit Batch" container — correctly retires the run it
  // replaced even though the two live in different containers. Falls back to this container's
  // rows when the global list isn't supplied. addWorkflowRunGroup always appends, so the LAST
  // occurrence of each key is the newest. getSupersedeCaseKey keeps multi-car drafting
  // positions (same attitude-state name, different following car) as distinct cases.
  const globalRunGroups =
    allWorkflowRunGroups && allWorkflowRunGroups.length > 0
      ? allWorkflowRunGroups
      : workflowRunGroups
  const supersededIds = computeSupersededIds(globalRunGroups)
  const isSuperseded = (wrg: WorkflowRunGroup): boolean => supersededIds.has(wrg.systemRunId)

  // Only show the drafting offset/Lead-Follow columns when this group actually has Drafting cases
  const hasDraftingCases = workflowRunGroups.some(
    wrg => !!wrg.runGroupCase?.selectedFollowingCar?.localPosition
  )

  // Split into active and prior (superseded) cases
  const activeCases = workflowRunGroups.filter(wrg => !supersededIds.has(wrg.systemRunId))
  const priorCases = workflowRunGroups.filter(wrg => supersededIds.has(wrg.systemRunId))

  // Compute compliance solve counts grouped by attitude state name.
  // Uses resubmitAttempts which is set from ATP_Report row count in blob storage via processSolveCount.
  // Compliance rule: ceil(rawSolves / 2)
  const complianceSolvesByState: Record<string, { raw: number; compliance: number }> = {}
  workflowRunGroups.forEach(wrg => {
    const stateName = wrg.runGroupCase?.selectedAttitudeState?.name
    if (stateName) {
      if (!complianceSolvesByState[stateName]) {
        complianceSolvesByState[stateName] = { raw: 0, compliance: 0 }
      }
      const attempts = parseInt(wrg.resubmitAttempts || '0', 10) || 0
      complianceSolvesByState[stateName].raw += attempts
    }
  })
  Object.values(complianceSolvesByState).forEach(entry => {
    entry.compliance = Math.ceil(entry.raw / 2)
  })

  // Helper function to get resubmit limit based on race team
  const getResubmitLimit = (raceTeamId: string): number => {
    // NASCAR-Cup: solve step may be re-run at most 2 times per case lineage
    return raceTeamId === 'NASCAR-Cup' ? 2 : Infinity
  }

  // Helper function to check if a case can be resubmitted (solve step)
  const canResubmitCase = (workflowRunGroup: WorkflowRunGroup): boolean => {
    const status = determineWorkflowGroupStatus(workflowRunGroup)
    // Allow resubmit for terminal cases. Cancellation can be present either
    // on derived workflow status or on the top-level run group overallStatus.
    return (
      status === RunStatus.COMPLETED ||
      status === RunStatus.ERROR ||
      status === RunStatus.WARN_ON_ERROR ||
      status === RunStatus.CANCELLED ||
      workflowRunGroup.overallStatus === RunStatus.CANCELLED
    )
  }

  // Handle resubmit for a single case
  const handleResubmitCase = async (workflowRunGroup: WorkflowRunGroup) => {
    if (!canResubmitCase(workflowRunGroup)) {
      toast.error('Resubmit limit reached for this case')
      return
    }

    console.log('[CaseGroupTable] Opening resubmit modal with data:', {
      name: workflowRunGroup.name,
      simTemplateFile: workflowRunGroup.runGroupCase?.simTemplate?.file,
      simTemplatePath: workflowRunGroup.runGroupCase?.simTemplate?.path
    })

    // Open the resubmit modal with this case
    setResubmitCaseData(workflowRunGroup)
    setShowResubmitModal(true)
  }

  // Handle confirmation of resubmit with optional SIM template update
  const handleResubmitConfirm = async (data: {
    simTemplate: CFDFile | null
    uploadedFile: File | null
    simChangeReason?: string
  }) => {
    if (!resubmitCaseData || isSubmittingResubmit) return
    setIsSubmittingResubmit(true)

    // Refresh methodology to latest version before resubmitting
    try {
      const refreshParams: any = { runId }
      await refreshMethodology(refreshParams)
    } catch (error) {
      console.error('Failed to refresh methodology:', error)
      toast.error('Failed to refresh methodology. Resubmit aborted.')
      setShowResubmitModal(false)
      setResubmitCaseData(null)
      return
    }

    // Add a fresh workflow run group for this specific case, optionally with new SIM template or file
    try {
      const resubmitParams: any = {
        runId,
        caseNames: [resubmitCaseData.name],
        // Target the exact run group so the server clones this specific following-car case
        // (multi-car drafting) rather than re-expanding all following cars from the generic
        // selectedCases entry.
        sourceSystemRunId: resubmitCaseData.systemRunId,
        simTemplate: data.simTemplate || undefined,
        uploadedFile: data.uploadedFile || undefined
      }

      await addWorkflowRunGroup(resubmitParams)

      const caseSource = resubmitCaseData.runGroupCase
      const caseName = caseSource?.name || resubmitCaseData.name

      // If the user explicitly changed the template, also patch selectedCases[n].simTemplate
      // so the stored case default stays in sync and future resubmits use the right template.
      if (caseSource && data.simTemplate) {
        try {
          const updatedCase = cloneRunConfigCase(caseSource as RunConfigCase)
          updatedCase.simTemplate = new CFDFile(data.simTemplate)
          const dataToPost = protoService.encodePayload(updatedCase, 'runconfigcase')
          if (dataToPost?.data_base64) {
            await updateRunConfigCase({ id: runId, data: dataToPost } as any)
          }
        } catch (patchErr) {
          // Non-fatal — the resubmit already succeeded; log but don't surface to user
          console.warn('Failed to patch selectedCases simTemplate:', patchErr)
        }
      }

      // Persist the SIM change reason if provided
      if (data.simChangeReason) {
        const previousSimFile = caseSource?.simTemplate?.file || ''
        const newSimFile = data.uploadedFile?.name || data.simTemplate?.file || ''
        await addSimChangeMetadata(runId, {
          caseName,
          previousSimFile,
          newSimFile,
          reason: data.simChangeReason
        }).catch(err => console.warn('Failed to persist SIM change metadata:', err))

        // Refresh local records
        getSimChangeMetadata(runId)
          .then(records => setSimChangeRecords(records || []))
          .catch(() => {})
      }

      toast.success(`Successfully resubmitted case "${resubmitCaseData.name}"`)
      setTimeout(() => toast.dismiss(), 2000)
    } catch (error) {
      console.error('Error resubmitting case:', error)
      toast.error(`Failed to resubmit case "${resubmitCaseData.name}"`)
    } finally {
      setIsSubmittingResubmit(false)
      setShowResubmitModal(false)
      setResubmitCaseData(null)
    }
  }

  // Persist an uploaded case SIM template as soon as the upload succeeds (before the user
  // confirms the resubmit) so it survives a page refresh. The case is captured via the
  // resubmitCaseData closure at upload-start time, so a later modal close won't lose it.
  const handleSimUploaded = async (template: CFDFile) => {
    const caseSource = resubmitCaseData?.runGroupCase
    if (!caseSource) return
    try {
      const updatedCase = cloneRunConfigCase(caseSource as RunConfigCase)
      updatedCase.simTemplate = new CFDFile(template)
      const dataToPost = protoService.encodePayload(updatedCase, 'runconfigcase')
      if (dataToPost?.data_base64) {
        await updateRunConfigCase({ id: runId, data: dataToPost } as any)
      }
    } catch (err) {
      toast.error('Failed to persist uploaded case simTemplate.')
      console.warn('Failed to persist uploaded case simTemplate:', err)
    }
  }

  let caseDetail = {}
  if (caseNameToShow) {
    const grp = workflowRunGroups.find(item => item.name === caseNameToShow)
    if (grp) {
      caseDetail = {
        ...grp.runGroupCase
      }
    }
  }

  const handleCaseMAVClick = (workflowRunGroup: WorkflowRunGroup) => {
    const result = getMAVCase(workflowRunGroup, false)
    if (typeof result === 'string') {
      toast.error(result)
      return
    }

    if (result === undefined) {
      toast.error('MAV Case not found.')
      return
    }

    result.DataPath = `${result.DataPath}Results/MAV_${result.ID}`

    const param = {
      Cases: [result]
    } as MAVParam
    console.log(param, JSON.stringify(param), encodeURIComponent(JSON.stringify(param)))
    window.location.href = `mav-launcher://${encodeURIComponent(JSON.stringify(param))}`
  }

  const buildColumns = (
    rows: DataTableExpandedRows,
    setRows: React.Dispatch<React.SetStateAction<DataTableExpandedRows>>
  ) => [
    {
      header: 'Name',
      field: 'name',
      headerClassName: CompactTableHeaderClassName,
      bodyClassName: 'id-cell',
      body: (rowData: WorkflowRunGroup) => {
        const rowName: string = rowData.name
        const rowKey: string = rowData.systemRunId
        const superseded = isSuperseded(rowData)
        const simChanged = !superseded && hasSimFileChanged(rowData)
        return (
          <div
            className={`flex items-center justify-between ${superseded ? 'opacity-50' : ''}`}
            ref={caseNameRef}
            id={`case-${rowName}`}>
            <div
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() => {
                // get current row id => if in expandRows, remove; else, append
                const copyExpandedRows: DataTableExpandedRows = { ...rows }
                if (rows[rowKey]) {
                  delete copyExpandedRows[rowKey]
                } else {
                  copyExpandedRows[rowKey] = true
                }
                setRows(copyExpandedRows)
              }}>
              {rows[rowKey] ? (
                <CollapseIcon className="fill-dark-blue dark:fill-light-blue h-6 w-6" />
              ) : (
                <ExpandIcon className="fill-dark-blue dark:fill-light-blue h-6 w-6" />
              )}
              {runCaseNameBodyTemplates(
                rowData.runGroupCase,
                determineWorkflowGroupStatus(rowData),
                currentVehicle
              )}
              {superseded && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded">
                  Initial Run
                </span>
              )}
              {simChanged && (
                <span
                  className="ml-2 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 rounded"
                  title="SIM file changed from previous run">
                  SIM file changed
                </span>
              )}
            </div>
          </div>
        )
      }
    },
    {
      header: 'FW',
      bodyClassName: 'w-[75px]',
      field: 'runGroupCase.selectedWingState.fwState',
      body: (rowData: WorkflowRunGroup) => {
        const num = rowData.runGroupCase.selectedWingState?.fwState
        return <span>{num && num.toFixed(1)}</span>
      },
      disabled: !isF1Team
    },
    {
      header: 'DRS(%)',
      bodyClassName: 'w-[75px]',
      field: 'runGroupCase.selectedWingState.drs',
      disabled: !isF1Team
    },
    {
      header: '#',
      field: 'runGroupCase',
      bodyClassName: 'w-[80px] max-w-[80px]',
      body: (rowData: WorkflowRunGroup) => {
        return <span>{rowData.systemRunId && rowData.systemRunId.replaceAll('0-', '')}</span>
      }
    },
    {
      header: 'Solves',
      field: 'resubmitAttempts',
      bodyClassName: 'w-[70px] max-w-[70px] text-center',
      body: (rowData: WorkflowRunGroup) => {
        const stateName = rowData.runGroupCase?.selectedAttitudeState?.name
        const entry = stateName ? complianceSolvesByState[stateName] : null
        if (!entry) return <span>0</span>
        return (
          <span title={`${entry.raw} raw solve(s) → ${entry.compliance} compliance solve(s)`}>
            {entry.compliance}
          </span>
        )
      },
      disabled: raceTeamId !== 'NASCAR-Cup' && raceTeamId !== 'testing-team-dev'
    },
    {
      header: 'Attitude',
      field: 'runGroupCase.selectedAttitudeState',
      bodyClassName: 'w-[120px]',
      body: (rowData: WorkflowRunGroup) => {
        return <div>{rowData.runGroupCase.selectedAttitudeState?.name}</div>
      }
    },
    {
      header: 'X Offset (L)',
      field: 'runGroupCase.selectedFollowingCar.localPosition.distance_x_m',
      headerClassName: 'whitespace-nowrap',
      bodyClassName: 'w-[130px]',
      body: (rowData: WorkflowRunGroup) => {
        const offsetInfo = getDraftingOffsetInfo(rowData.runGroupCase, currentVehicle)
        return <span>{offsetInfo ? offsetInfo.xOffset : ''}</span>
      },
      disabled: !hasDraftingCases
    },
    {
      header: 'Y Offset (W)',
      field: 'runGroupCase.selectedFollowingCar.localPosition.distance_y_m',
      headerClassName: 'whitespace-nowrap',
      bodyClassName: 'w-[130px]',
      body: (rowData: WorkflowRunGroup) => {
        const offsetInfo = getDraftingOffsetInfo(rowData.runGroupCase, currentVehicle)
        return <span>{offsetInfo ? offsetInfo.yOffset : ''}</span>
      },
      disabled: !hasDraftingCases
    },
    {
      header: 'Lead/Trail',
      field: 'runGroupCase.followingCarCase',
      headerClassName: 'whitespace-nowrap',
      bodyClassName: 'w-[110px]',
      body: (rowData: WorkflowRunGroup) => {
        const offsetInfo = getDraftingOffsetInfo(rowData.runGroupCase, currentVehicle)
        return <span>{offsetInfo?.leadOrTrail || ''}</span>
      },
      disabled: !hasDraftingCases
    },
    {
      header: 'Domain',
      field: 'runGroupCase.selectedDomainState',
      bodyClassName: 'w-[120px]',
      body: (rowData: WorkflowRunGroup) => {
        return <div>{rowData.runGroupCase.selectedDomainState?.name}</div>
      },
      disabled: !isF1Team
    },
    {
      header: 'Tyre Set',
      field: 'selectedTyreState',
      bodyClassName: 'w-[100px] min-w-[100px]',
      body: (rowData: WorkflowRunGroup) => {
        return <div>{rowData.runGroupCase.selectedTyreState?.name}</div>
      },
      disabled: !isF1Team
    },
    {
      header: 'Sim Type',
      field: 'runGroupCase.selectedSimulationState',
      bodyClassName: 'w-[100px] min-w-[100px]',
      body: (rowData: WorkflowRunGroup) => {
        return <div>{rowData.runGroupCase.selectedSimulationState?.name}</div>
      },
      disabled: !isF1Team
    },
    {
      header: 'FRH',
      field: 'runGroupCase.selectedAttitudeState.frh',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: WorkflowRunGroup) => {
        const num = rowData.runGroupCase.selectedAttitudeState?.frh
        return <span>{num && num.toFixed(3)}</span>
      }
    },
    {
      header: 'RRH',
      field: 'runGroupCase.selectedAttitudeState.rrh',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: WorkflowRunGroup) => {
        const num = rowData.runGroupCase.selectedAttitudeState?.rrh
        return <span>{num && num.toFixed(3)}</span>
      }
    },
    {
      header: 'Steer',
      field: 'runGroupCase.selectedAttitudeState.steer',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: WorkflowRunGroup) => {
        const num = rowData.runGroupCase.selectedAttitudeState?.steer
        return <span>{num && num.toFixed(3)}</span>
      }
    },
    {
      header: 'Roll',
      field: 'runGroupCase.selectedAttitudeState.roll',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: WorkflowRunGroup) => {
        const num = rowData.runGroupCase.selectedAttitudeState?.roll
        return <span>{num && num.toFixed(3)}</span>
      }
    },
    {
      header: 'Yaw',
      field: 'runGroupCase.selectedAttitudeState.yaw',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: WorkflowRunGroup) => {
        const num = rowData.runGroupCase.selectedAttitudeState?.yaw
        return <span>{num && num.toFixed(1)}</span>
      }
    },
    {
      header: 'Rad',
      field: 'runGroupCase.selectedAttitudeState.radius',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: WorkflowRunGroup) => {
        const num = rowData.runGroupCase.selectedAttitudeState?.radius
        return <span>{num && num.toFixed(3)}</span>
      }
    },
    {
      header: `Spd${speedType ? ` (${speedType})` : ''}`,
      field: 'runGroupCase.selectedAttitudeState.speed',
      bodyClassName: 'w-[90px] max-w-[90px]',
      body: (rowData: WorkflowRunGroup) => {
        const num = rowData.runGroupCase.selectedAttitudeState?.speed
        return <span>{num && num.toFixed(1)}</span>
      }
    },
    {
      header: 'Exh',
      field: 'runGroupCase.selectedAttitudeState.exhaust',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: WorkflowRunGroup) => {
        const num = rowData.runGroupCase.selectedAttitudeState?.exhaust
        return <span>{num ? num.toFixed(3) : 0}</span>
      }
    },
    {
      header: 'SPEC',
      field: 'runGroupCase.selectedAttitudeState.spec',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: WorkflowRunGroup) => {
        const num = (rowData.runGroupCase.selectedAttitudeState as any)?.spec
        return <span>{num != null ? num.toFixed(1) : ''}</span>
      },
      disabled: !NASCAR28Teams.includes(raceTeamId)
    },
    {
      header: 'RAD_BLOCKER',
      field: 'runGroupCase.selectedAttitudeState.radBlocker',
      bodyClassName: 'w-[75px] max-w-[75px]',
      body: (rowData: WorkflowRunGroup) => {
        const num = (rowData.runGroupCase.selectedAttitudeState as any)?.radBlocker
        return <span>{num != null ? num.toFixed(1) : ''}</span>
      },
      disabled: !NASCAR28Teams.includes(raceTeamId)
    }
  ]

  useEffect(() => {
    if (!selectedSystemRunId || workflowRunGroups.length === 0) {
      // Clear the guard when there is no active selection so that deep-linking
      // back to the same run ID later in the session re-triggers the
      // auto-expand/scroll instead of being short-circuited.
      if (!selectedSystemRunId) {
        handledSelectionRef.current = ''
      }
      return
    }

    // Only auto-expand and scroll to the deep-linked case once per selection.
    // The submission auto-refreshes every few seconds, producing a new
    // workflowRunGroups reference each time; without this guard the effect
    // would re-run on every poll and reset expandedRows/expandedPriorRows,
    // collapsing the workflow and task rows the user is actively reviewing.
    if (handledSelectionRef.current === selectedSystemRunId) {
      return
    }

    const matchedGroup = workflowRunGroups.find(group => {
      if (group.systemRunId === selectedSystemRunId) {
        return true
      }

      const legacyCaseId = group.systemRunId.split('-').pop()
      return legacyCaseId === selectedSystemRunId
    })

    if (matchedGroup) {
      handledSelectionRef.current = selectedSystemRunId

      if (supersededIds.has(matchedGroup.systemRunId)) {
        // Open the Prior Cases section and expand the row there
        setPriorSectionOpen(true)
        setExpandedPriorRows({ [matchedGroup.systemRunId]: true })
      } else {
        setExpandedRows({ [matchedGroup.systemRunId]: true })
      }

      setTimeout(() => {
        caseNameRef.current &&
          caseNameRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 500)
    }
  }, [selectedSystemRunId, workflowRunGroups])

  // Only resubmittable (terminal, non-superseded) cases are selectable for a bulk resubmit.
  const selectedCaseRows = activeCases.filter(
    wrg => canResubmitCase(wrg) && (selectedCaseIds || []).includes(wrg.systemRunId)
  )

  const handleSelectionChange = (e: { value: WorkflowRunGroup[] }) => {
    if (!onSelectedCaseIdsChange) {
      return
    }
    // Replace only this table's selectable ids; preserve selections made in sibling tables.
    const tableSelectableIds = new Set(
      activeCases.filter(canResubmitCase).map(wrg => wrg.systemRunId)
    )
    const otherTableIds = (selectedCaseIds || []).filter(id => !tableSelectableIds.has(id))
    const nextTableIds = (e.value || []).map(wrg => wrg.systemRunId)
    onSelectedCaseIdsChange([...otherTableIds, ...nextTableIds])
  }

  return (
    <>
      <DataTable
        value={activeCases}
        expandedRows={expandedRows}
        rowExpansionTemplate={StateRowExpansionTemplate(
          runId,
          workflowRunGroups,
          expandedRows,
          setExpandedRows,
          currentUser?.locale,
          submittingTask || isSubmittingResubmit,
          showAdditionalReferenceRunsModal,
          setShowAdditionalReferenceRunsModal,
          supersededIds
        )}
        dataKey="systemRunId"
        editMode="row"
        selectOnEdit={true}
        selectionMode="multiple"
        selection={selectedCaseRows}
        onSelectionChange={handleSelectionChange}
        isDataSelectable={(e: any) => !!e.data && canResubmitCase(e.data as WorkflowRunGroup)}
        pt={{
          bodyRow: {
            className: 'table-row'
          }
        }}>
        <Column
          selectionMode="multiple"
          headerStyle={{ width: '3rem' }}
          bodyClassName="!text-left !pl-1"
          headerClassName={classNames(
            CompactTableHeaderClassName,
            '[&_.p-column-header-content]:justify-center'
          )}
        />
        {buildColumns(expandedRows, setExpandedRows).map(item => {
          if (item.disabled) {
            return null
          }
          return (
            <Column
              className={classNames({ 'case-name': item.field === 'name' })}
              key={item.field}
              field={item.field}
              header={item.header}
              headerClassName={item.headerClassName}
              bodyClassName={item.bodyClassName}
              body={item.body}></Column>
          )
        })}

        <Column
          rowEditor
          headerClassName="one-col-editor-header editor-header"
          bodyClassName="w-[150px]"
          pt={{
            root: {
              className: 'px-2 py-2 text-center'
            }
          }}
          bodyStyle={{ textAlign: 'right', paddingBottom: 24 }}
          body={(rowData, options) => {
            const rowName: string = rowData.name
            const workflowRunGroup: WorkflowRunGroup = rowData as WorkflowRunGroup

            const superseded = isSuperseded(rowData as WorkflowRunGroup)
            const showMAVBtn = isRunGroupMAVCompleted(workflowRunGroup as WorkflowRunGroup)

            return (
              <div
                className={`flex items-center justify-center min-w-full min-h-full space-x-1 ${
                  superseded ? 'opacity-50' : ''
                }`}>
                <IconButton
                  severity="info"
                  icon={<EyeIcon className="fill-white dark:fill-light-blue h-4 w-4" />}
                  onClick={() => {
                    setShowCaseDetail(true)
                    setCaseNameToShow(rowName)
                  }}
                  title="View Case Details"
                />
                {workflowRunGroup && !superseded && (
                  <ViewLogsButton
                    workflowRunGroup={workflowRunGroup}
                    raceTeamId={raceTeamId}
                    onModalStateChange={onLogModalStateChange}
                  />
                )}
                {workflowRunGroup &&
                  !superseded &&
                  userCanMutate &&
                  canResubmitCase(workflowRunGroup) && (
                    <IconButton
                      title="Resubmit Case"
                      severity="danger"
                      icon={<RedoIcon className="fill-white h-4 w-4" />}
                      onClick={() => handleResubmitCase(workflowRunGroup)}
                      disabled={submittingTask}
                    />
                  )}
                {showMAVBtn && !superseded && (
                  <IconButton
                    title="MAV"
                    severity="secondary"
                    icon={<MAVIcon className="fill-white dark:fill-light-blue h-4 w-8" />}
                    onClick={() => handleCaseMAVClick(rowData)}
                  />
                )}

                {workflowRunGroup &&
                  !superseded &&
                  userCanMutate &&
                  workflowRunGroup.overallStatus === RunStatus.CREATED && (
                    <IconButton
                      title="Edit"
                      severity="secondary"
                      onClick={() => {
                        onEditCase(
                          submissionGroupName,
                          rowName,
                          options.rowIndex,
                          rowData.runGroupCase?.key
                        )
                      }}
                      icon={<EditIcon className="fill-white w-4 h-4" />}
                    />
                  )}
                {workflowRunGroup &&
                  !superseded &&
                  userCanMutate &&
                  workflowRunGroup.overallStatus === RunStatus.CREATED && (
                    <IconButton
                      icon={<DeleteIcon className="fill-white m-0.5 h-3 w-3" />}
                      severity="danger"
                      onClick={() => {
                        onDeleteCase(
                          submissionGroupName,
                          rowName,
                          options.rowIndex,
                          rowData.runGroupCase?.key
                        )
                      }}
                      title="Remove Case"
                    />
                  )}
              </div>
            )
          }}
        />
      </DataTable>

      {priorCases.length > 0 && (
        <div className="mt-4 border border-gray-200 dark:border-neutral-70 rounded">
          <div
            className="flex items-center gap-2 px-4 py-2 cursor-pointer bg-neutral-20 dark:bg-neutral-80 select-none"
            onClick={() => setPriorSectionOpen(prev => !prev)}>
            {priorSectionOpen ? (
              <CollapseIcon className="fill-dark-blue dark:fill-light-blue h-5 w-5" />
            ) : (
              <ExpandIcon className="fill-dark-blue dark:fill-light-blue h-5 w-5" />
            )}
            <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
              Prior Cases ({priorCases.length})
            </span>
          </div>
          {priorSectionOpen && (
            <DataTable
              value={priorCases}
              expandedRows={expandedPriorRows}
              rowExpansionTemplate={StateRowExpansionTemplate(
                runId,
                workflowRunGroups,
                expandedPriorRows,
                setExpandedPriorRows,
                currentUser?.locale,
                submittingTask || isSubmittingResubmit,
                showAdditionalReferenceRunsModal,
                setShowAdditionalReferenceRunsModal,
                supersededIds
              )}
              dataKey="systemRunId"
              editMode="row"
              selectOnEdit={true}
              pt={{
                bodyRow: {
                  className: 'table-row opacity-60'
                }
              }}>
              {buildColumns(expandedPriorRows, setExpandedPriorRows).map(item => {
                if (item.disabled) {
                  return null
                }
                return (
                  <Column
                    className={classNames({ 'case-name': item.field === 'name' })}
                    key={item.field}
                    field={item.field}
                    header={item.header}
                    headerClassName={item.headerClassName}
                    bodyClassName={item.bodyClassName}
                    body={item.body}></Column>
                )
              })}

              <Column
                rowEditor
                headerClassName="one-col-editor-header editor-header"
                bodyClassName="w-[150px]"
                pt={{
                  root: {
                    className: 'px-2 py-2 text-center'
                  }
                }}
                bodyStyle={{ textAlign: 'right', paddingBottom: 24 }}
                body={rowData => {
                  const rowName: string = rowData.name
                  const workflowRunGroup: WorkflowRunGroup = rowData as WorkflowRunGroup

                  return (
                    <div className="flex items-center justify-center min-w-full min-h-full space-x-1">
                      <IconButton
                        severity="info"
                        icon={<EyeIcon className="fill-white dark:fill-light-blue h-4 w-4" />}
                        onClick={() => {
                          setShowCaseDetail(true)
                          setCaseNameToShow(rowName)
                        }}
                        title="View Case Details"
                      />
                      {workflowRunGroup && (
                        <ViewLogsButton
                          workflowRunGroup={workflowRunGroup}
                          raceTeamId={raceTeamId}
                          onModalStateChange={onLogModalStateChange}
                        />
                      )}
                    </div>
                  )
                }}
              />
            </DataTable>
          )}
        </div>
      )}
      {showPeriviewModal && (
        <PeriviewModal
          caseName={caseNameForPariview as string}
          caseId={caseIdForPariview as string}
          initReference={baselineRunId}
          onCancel={() => setShowPeriviewModal(false)}
          onConfirm={() => setShowPeriviewModal(false)}
        />
      )}

      <ResubmitCaseModal
        isOpen={showResubmitModal}
        caseName={resubmitCaseData?.name || ''}
        submissionId={runId}
        submissionName={submissionGroupName}
        currentSimTemplate={resubmitCaseData?.runGroupCase?.simTemplate || null}
        isLoading={submittingTask || isSubmittingResubmit}
        onConfirm={handleResubmitConfirm}
        onSimUploaded={handleSimUploaded}
        onCancel={() => {
          setShowResubmitModal(false)
          setResubmitCaseData(null)
        }}
      />

      <Dialog
        header={`${caseNameToShow} Details`}
        visible={showCaseDetail}
        closeOnEscape
        pt={{
          root: {
            className: 'w-[60vw]'
          },
          mask: {
            className: 'bg-blue-backdrop dark:bg-neutral-80'
          },
          content: {
            className: 'max-h-[80vh] overflow-y-auto'
          }
        }}
        onHide={() => {
          if (!showCaseDetail) return
          setShowCaseDetail(false)
        }}>
        {/* SIM File Change Banner */}
        {caseNameToShow &&
          (() => {
            const wrg = workflowRunGroups.find(item => item.name === caseNameToShow)
            if (!wrg) return null
            const simChanged = hasSimFileChanged(wrg)
            const changeRecord = getSimChangeRecord(wrg)
            if (!simChanged && !changeRecord) return null
            return (
              <div className="mb-4 p-3 rounded border border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-600">
                <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400 font-semibold text-sm">
                  <i className="pi pi-exclamation-triangle" />
                  SIM file changed
                </div>
                {changeRecord?.reason && (
                  <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Reason:</span>{' '}
                    <span className="italic">{changeRecord.reason}</span>
                  </div>
                )}
                {changeRecord?.previousSimFile && (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Previous: {changeRecord.previousSimFile} → New: {changeRecord.newSimFile}
                  </div>
                )}
                {changeRecord?.timestamp && (
                  <div className="mt-1 text-xs text-gray-400">
                    Changed: {new Date(changeRecord.timestamp).toLocaleString()}
                    {changeRecord.changedBy && ` by ${changeRecord.changedBy}`}
                  </div>
                )}
              </div>
            )
          })()}
        <p>
          <pre className="whitespace-pre-wrap">{caseDetail && safeStringify(caseDetail, 4)}</pre>
        </p>
      </Dialog>
      {showErrorResultModal && (
        <Modal
          title="Rescale Job Error Log"
          isOpen={showErrorResultModal}
          withAction={false}
          showClose={true}
          easyClose={true}
          onClose={() => setShowErrorResultModal(false)}
          onCancel={() => setShowErrorResultModal(false)}>
          <pre
            className="whitespace-pre-wrap overflow-y-auto max-h-[70vh]"
            style={{ maxHeight: 'calc(100%-100px) !important' }}>
            {errorResultText}
          </pre>
        </Modal>
      )}
    </>
  )
}

export default CaseGroupTable
